import { Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { PaginatorModule, PaginatorState } from 'primeng/paginator';
import { TypeChipComponent } from '@shared/components';
import { LessonsFacade } from '@app/store/lessons';
import { QuizzesFacade } from '@app/store/quizzes';
import { ReflectionsFacade } from '@app/store/reflections';
import { Lesson } from '@app/store/shared/models';
import { Quiz } from '@app/store/quizzes/models/quiz';
import { Reflection } from '@app/store/reflections/models/reflection';
import { DialogSearchComponent } from '../shared/dialog-search/dialog-search.component';
import { ItemSelectionService } from './item-selection.service';

const LESSON_ICONS: Record<string, string> = {
    video: 'pi pi-video',
    audio: 'pi pi-headphones',
    text: 'pi pi-file-edit',
};

type DialogTab = 'lessons' | 'quizzes' | 'reflections';

@Component({
    selector: 'app-select-item-dialog',
    templateUrl: './select-item.component.html',
    styleUrls: ['./select-item.component.css'],
    imports: [TranslateModule, RouterLink, ButtonModule, PaginatorModule, TypeChipComponent, DialogSearchComponent],
})
export class SelectItemComponent implements OnInit, OnDestroy {
    private readonly router = inject(Router);
    private readonly translateService = inject(TranslateService);
    private readonly lessonsFacade = inject(LessonsFacade);
    private readonly quizzesFacade = inject(QuizzesFacade);
    private readonly reflectionsFacade = inject(ReflectionsFacade);
    private readonly itemSelectionService = inject(ItemSelectionService);

    readonly tab = signal<DialogTab>('lessons');
    readonly searchValue = signal('');

    readonly lessons = this.lessonsFacade.lessons;
    readonly lessonsTotal = this.lessonsFacade.total;
    readonly lessonsLoading = this.lessonsFacade.loading;

    readonly quizzes = this.quizzesFacade.quizzes;
    readonly quizzesTotal = this.quizzesFacade.total;
    readonly quizzesLoading = this.quizzesFacade.loading;

    readonly reflections = this.reflectionsFacade.reflections;
    readonly reflectionsTotal = this.reflectionsFacade.total;
    readonly reflectionsLoading = this.reflectionsFacade.loading;

    readonly lessonIcon = (type: string): string => LESSON_ICONS[type] ?? 'pi pi-file';

    readonly limit = 10;
    readonly lessonsFirst = signal(0);
    readonly quizzesFirst = signal(0);
    readonly reflectionsFirst = signal(0);

    readonly searchPlaceholder = this.translateService.instant('modules.items.dialog.search');

    ngOnInit(): void {
        this.loadLessons();
    }

    ngOnDestroy(): void {
        this.lessonsFacade.clear();
        this.quizzesFacade.clear();
        this.reflectionsFacade.clear();
    }

    setTab(tab: DialogTab): void {
        this.tab.set(tab);
        if (tab === 'lessons') {
            this.lessonsFirst.set(0);
            this.loadLessons();
        } else if (tab === 'quizzes') {
            this.quizzesFirst.set(0);
            this.loadQuizzes();
        } else {
            this.reflectionsFirst.set(0);
            this.loadReflections();
        }
    }

    onSearch(search: string): void {
        this.searchValue.set(search);
        if (this.tab() === 'lessons') {
            this.lessonsFirst.set(0);
            this.loadLessons();
        } else if (this.tab() === 'quizzes') {
            this.quizzesFirst.set(0);
            this.loadQuizzes();
        } else {
            this.reflectionsFirst.set(0);
            this.loadReflections();
        }
    }

    selectLesson(lesson: Lesson): void {
        this.itemSelectionService.select({ id: String(lesson.id), type: 'lesson' });
        this.close();
    }

    selectQuiz(quiz: Quiz): void {
        this.itemSelectionService.select({ id: quiz.id, type: 'quiz' });
        this.close();
    }

    selectReflection(reflection: Reflection): void {
        this.itemSelectionService.select({ id: reflection.id, type: 'reflection' });
        this.close();
    }

    onLessonsPageChange(event: PaginatorState): void {
        const first = event.first ?? 0;
        this.lessonsFirst.set(first);
        const page = Math.floor(first / this.limit) + 1;
        this.loadLessons(page);
    }

    onQuizzesPageChange(event: PaginatorState): void {
        const first = event.first ?? 0;
        this.quizzesFirst.set(first);
        const page = Math.floor(first / this.limit) + 1;
        this.loadQuizzes(page);
    }

    onReflectionsPageChange(event: PaginatorState): void {
        const first = event.first ?? 0;
        this.reflectionsFirst.set(first);
        const page = Math.floor(first / this.limit) + 1;
        this.loadReflections(page);
    }

    loadLessons(page = 1): void {
        this.lessonsFacade.getLessons({ page, limit: this.limit, search: this.searchValue() || undefined });
    }

    loadQuizzes(page = 1): void {
        this.quizzesFacade.getQuizzes({ page, limit: this.limit, search: this.searchValue() || undefined });
    }

    loadReflections(page = 1): void {
        this.reflectionsFacade.getReflections({ page, limit: this.limit, search: this.searchValue() || undefined });
    }

    close(): void {
        this.router.navigate([{ outlets: { dialog: null } }]);
    }
}
